/** @jsxImportSource ./simboluri-jsx */
import { textVerdict } from "./elemente";
import { useEffect, useMemo, useState } from "react";
import { StepState, fmtDate } from "./tipuri";
import { CotaH, CotaV, mFmt, cmFmt } from "./cote";
import Info from "./Info";
import TitluVerificare, { useCatalogVerificari } from "./Verificare";
import { Focus, hashSectiune } from "./legaturi";
import { DescriereaPasului } from "./TitluPas";
import InfasuratoareAx from "./InfasuratoareAx";
import AxFundare3D from "./AxFundare3D";
import {
  AxArmat,
  Bare,
  RezultatArmareFundatie as Rezultat,
} from "./armareAx";
import { t, tx, useT, type Cheie } from "../i18n";
import { traduceRezultat } from "./texteRezultat";

// Pasul 18 — Armarea grinzilor de fundare, per AX.
//
// Elementul paginii e AXUL de fundație (`F-Ax 4`) — același id pe care îl
// scrie extrasul de armătură și cu care 3D-ul numește piesa. Trei lucruri se
// citesc împreună, fiindcă structuristul le gândește împreună:
//
//   * SECȚIUNEA cotată, cu barele CHIAR alese (talpă + inimă, longitudinale
//     sus/jos, etrierul inimii, plasa consolei) — desenul e prima verificare;
//   * PLANȘA AXULUI (`InfasuratoareAx`) și vederea 3D a piesei: de pe
//     armătura nu e constantă pe tot axul, deci se poate în
//     sfârșit desena unde se taie barele și unde se îndesesc etrierii;
//   * EFORTURILE care au cerut-o (M⁺, M⁻, V, plus momentul cvasipermanent al
//     fisurii), cu combinația guvernantă;
//   * VERDICTELE, cu explicațiile venite din `catalog_verificari.py`.
//
// Nicio explicație de verificare nu se scrie aici: vine din catalog, prin
// <TitluVerificare>. Nici „ce face pasul": <DescriereaPasului>.

// Tipurile stau în `armareAx.ts`: le citesc și planșa înfășurătoarei, și
// vederea 3D, iar ținute aici ar fi trebuit fie importate din pagină de
// componentele ei (import circular), fie copiate.

const CULORI = {
  beton: "#e7c07a",
  betonLinie: "#c98a2b",
  pamant: "#e6ded1",
  pamantLinie: "#b9ab93",
  otel: "#b5323d",
  etrier: "#2e5d8a",
};

function clasaVerdict(v: string): string {
  if (String(v).startsWith("SE VERIFICA")) return "st-ok";
  if (String(v).startsWith("ATENTIE")) return "st-warn";
  return "st-bad";
}

function Verdict({ v }: { v: string }) {
  return <span className={clasaVerdict(v)}>{textVerdict(v)}</span>;
}

// Fața barelor, cum vine de la server („jos"/„sus"), în limba ecranului.
const FATA: Record<string, Cheie> = {
  jos: "struct.arp.fata_jos",
  sus: "struct.arp.fata_sus",
};
function fataText(f: string): string {
  return FATA[f] ? t(FATA[f]) : f;
}

function nr(x: number | null | undefined, z = 0): string {
  if (x == null || Number.isNaN(Number(x))) return "—";
  return Number(x).toFixed(z);
}

// ---------------------------------------------------------------------------
// Secțiunea armată, cotată
// ---------------------------------------------------------------------------

/** Secțiunea transversală a grinzii de fundare, cu armătura CHIAR aleasă.
 *
 *  Se desenează la scară, din cifrele pasului: talpa B × h_t, inima
 *  b_w × (H − h_t), barele longitudinale pe rândurile lor, etrierul
 *  perimetral și plasa transversală a tălpii. Convenția de culoare e cea a
 *  celorlalte desene: galben = beton, roșu = oțel longitudinal, albastru =
 *  armătură transversală, bej = pământul de pe umerii tălpii. */
function SectiuneArmata({ ax }: { ax: AxArmat }) {
  const { B_m: B, H_c_m: H, h_talpa_m: ht, b_inima_m: bw } = ax;
  const hi = Math.max(H - ht, 0);
  const c = ax.acoperire_mm / 1000;

  const W = 340;
  const k = Math.min(190 / Math.max(B, 0.01), 150 / Math.max(H, 0.01));
  const bx = (B * k) / 2;
  const cx = W / 2;
  const y0 = 26;                        // fața de sus a inimii
  const yTalpaSus = y0 + hi * k;
  const yJos = yTalpaSus + ht * k;
  const Ht = yJos + 44;

  const rBara = (phi: number) => Math.max(((phi / 1000) * k) / 2, 1.6);

  const barele = (b: Bare, sus: boolean) => {
    const randuri = Math.max(b.randuri ?? 1, 1);
    const perRand = Math.ceil(b.n / randuri);
    const r = rBara(b.phi_mm);
    const xIn = cx - (bw * k) / 2 + c * k + r;
    const xOut = cx + (bw * k) / 2 - c * k - r;
    const puncte: { x: number; y: number }[] = [];
    for (let i = 0; i < randuri; i++) {
      const n =
        i === randuri - 1 ? b.n - perRand * (randuri - 1) : perRand;
      if (n <= 0) continue;
      const dy = c * k + r + i * 2.6 * r;
      const y = sus ? y0 + dy : yJos - dy;
      for (let j = 0; j < n; j++) {
        const t = n === 1 ? 0.5 : j / (n - 1);
        puncte.push({ x: xIn + t * (xOut - xIn), y });
      }
    }
    return puncte.map((p, i) => (
      <circle
        key={`${sus ? "s" : "j"}${i}`}
        cx={p.x}
        cy={p.y}
        r={r}
        fill={CULORI.otel}
      />
    ));
  };

  const pl = ax.armare.plasa_talpa;
  const consola = Math.max((B - bw) / 2, 0);

  return (
    <figure className="dg-fig">
      <svg
        viewBox={`0 0 ${W} ${Ht}`}
        className="dg-svg"
        role="img"
        aria-label={t("struct.arf.aria_sectiune", { ax: ax.ax })}
      >
        {consola > 0.01 && hi > 0.01 && (
          <>
            <rect
              x={cx - bx}
              y={y0}
              width={consola * k}
              height={hi * k}
              fill={CULORI.pamant}
              stroke={CULORI.pamantLinie}
              strokeWidth={0.5}
              strokeDasharray="2 2"
            />
            <rect
              x={cx + (bw * k) / 2}
              y={y0}
              width={consola * k}
              height={hi * k}
              fill={CULORI.pamant}
              stroke={CULORI.pamantLinie}
              strokeWidth={0.5}
              strokeDasharray="2 2"
            />
          </>
        )}
        {hi > 0.001 && (
          <rect
            x={cx - (bw * k) / 2}
            y={y0}
            width={bw * k}
            height={hi * k}
            fill={CULORI.beton}
            stroke={CULORI.betonLinie}
            strokeWidth={1}
          />
        )}
        <rect
          x={cx - bx}
          y={yTalpaSus}
          width={B * k}
          height={ht * k}
          fill={CULORI.beton}
          stroke={CULORI.betonLinie}
          strokeWidth={1}
        />
        {hi > 0.001 && (
          <rect
            x={cx - (bw * k) / 2 + c * k}
            y={y0 + c * k}
            width={bw * k - 2 * c * k}
            height={(hi + ht) * k - 2 * c * k}
            fill="none"
            stroke={CULORI.etrier}
            strokeWidth={1.2}
            rx={2}
          />
        )}
        {pl && (
          <line
            x1={cx - bx + c * k}
            y1={yJos - c * k}
            x2={cx + bx - c * k}
            y2={yJos - c * k}
            stroke={CULORI.etrier}
            strokeWidth={1.4}
          />
        )}
        {barele(ax.armare.jos, false)}
        {barele(ax.armare.sus, true)}

        <CotaH
          x1={cx - bx}
          x2={cx + bx}
          y={yJos + 24}
          martorY={yJos}
          text={mFmt(B)}
        />
        <CotaH
          x1={cx - (bw * k) / 2}
          x2={cx + (bw * k) / 2}
          y={y0 - 12}
          martorY={y0}
          text={mFmt(bw)}
          deasupra
        />
        <CotaV
          x={cx + bx + 24}
          y1={y0}
          y2={yJos}
          martorX={cx + bx}
          text={mFmt(H)}
          laDreapta
        />
        <CotaV
          x={cx - bx - 16}
          y1={yTalpaSus}
          y2={yJos}
          martorX={cx - bx}
          text={cmFmt(ht)}
        />
      </svg>
      <figcaption className="dg-cap">
        {tx("struct.arf.sectiune_cap", {
          ax: ax.ax, df: mFmt(ax.Df_m),
          jn: ax.armare.jos.n, jphi: ax.armare.jos.phi_mm,
          sn: ax.armare.sus.n, sphi: ax.armare.sus.phi_mm,
          ephi: ax.armare.etrieri.phi_mm, epas: ax.armare.etrieri.pas_mm,
          ramuri: ax.armare.etrieri.ramuri,
        })}
        {pl && (
          <>
            {" "}
            {t("struct.arf.sectiune_plasa", { phi: pl.phi_mm, pas: pl.pas_mm })}
          </>
        )}
        {t("struct.arf.sectiune_acoperire", { c: ax.acoperire_mm, d: mFmt(ax.d_m) })}
      </figcaption>
    </figure>
  );
}

// ---------------------------------------------------------------------------
// Pagina
// ---------------------------------------------------------------------------

/** Nota de armare a unui ax de fundare: secțiunea armată, detalierea,
 *  barele suplimentare, etrierii pe zone, eforturile, armarea aleasă și
 *  pozițiile. `faraVederi` lasă deoparte înfășurătoarea, piesa 3D și tabelul
 *  verificărilor: pagina elementului le are deja, deasupra. */
export function CardAx({ ax, r, projectId, solutie, faraVederi }: {
  ax: AxArmat;
  r: Rezultat;
  projectId?: string;
  solutie?: string | null;
  faraVederi?: boolean;
}) {
  useT();
  const catalog = useCatalogVerificari();
  return (
        <div className="st-findings">
          <h3>
            {t("struct.arf.grinda_fundare_ax", { ax: ax.ax })}{" "}
            <span className="sg-muted">({ax.element})</span>{" "}
            <Verdict v={ax.verdict} />
          </h3>

          <SectiuneArmata ax={ax} />

          <p className="sg-muted">
            {t("struct.arf.cota_axului")}
            {projectId && (
              <>
                {" "}
                {t("struct.arf.geometria_vine_din")}{" "}
                <a
                  href={hashSectiune(projectId, "verificare_fundatie", solutie)}
                >
                  {t("struct.pas.scurt", { nr: 17 })}
                </a>
                {t("struct.arf.eforturile_din")}{" "}
                <a href={hashSectiune(projectId, "eforturi", solutie)}>
                  {t("struct.pas.scurt", { nr: 12 })}
                </a>
                .
              </>
            )}
          </p>

          {!faraVederi && (<>
          <h4>{t("struct.arf.infasuratoarea_lungime")}</h4>
          <InfasuratoareAx
            ax={ax}
            croire={(r.croire ?? []).find((c) => c.ax === ax.ax) ?? null}
          />
          </>)}
          {ax.detaliere && (
            <p className="sg-muted">
              {t("struct.arf.detaliere_aici", {
                regula: ax.detaliere.regula,
                al: mFmt(ax.detaliere.a_l_m),
                cot: ax.detaliere.cot_theta,
                z: mFmt(ax.detaliere.zona_reazem_m),
              })}
            </p>
          )}

          {!faraVederi && (<>
          <h4>{t("struct.arf.piesa_spatiu")}</h4>
          <AxFundare3D ax={ax} />
          <p className="sg-muted">
            {t("struct.arf.albastru_carcasa_continu")}
          </p>
          </>)}

          {!!(ax.armare.suplimentare?.jos?.length ||
            ax.armare.suplimentare?.sus?.length) && (
            <>
              <h4>{t("struct.arf.barele_suplimentare_unde")}</h4>
              <table className="sg-table">
                <thead>
                  <tr>
                    <th>{t("struct.arf.fata")}</th>
                    <th>{t("struct.arf.th_bara")}</th>
                    <th>{t("struct.arf.th_as_cerut_acolo")}</th>
                    <th>{t("struct.arf.lucreaza")}</th>
                    <th>{t("struct.arf.bara_croita")}</th>
                    <th>{t("struct.arf.th_prelungirea")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(["jos", "sus"] as const).flatMap((fata) =>
                    (ax.armare.suplimentare?.[fata] ?? []).map((z, i) => (
                      <tr key={`${fata}${i}`}>
                        <td>{fataText(fata)}</td>
                        <td className="sg-mono">
                          {z.n}Ø{z.phi_mm}
                          {(z.randuri_cu_continue ?? 1) > 1 && (
                            <span className="sg-muted"> {t("struct.arf.randul_2")}</span>
                          )}
                        </td>
                        <td className="sg-mono">{z.As_necesar_cm2} cm²</td>
                        <td className="sg-mono">
                          {mFmt(z.s1_teoretic_m)}…{mFmt(z.s2_teoretic_m)} m
                        </td>
                        <td className="sg-mono">
                          {mFmt(z.s1_m)}…{mFmt(z.s2_m)} m ({mFmt(z.L_m)} m)
                        </td>
                        <td className="sg-muted">
                          {t("struct.arf.prelungire_fiecare_parte", {
                            al: mFmt(z.a_l_m), lbd: mFmt(z.l_bd_m), p: mFmt(z.prelungire_m),
                          })}
                          {z.la_capat && " " + t("struct.arf.atinge_capatul")}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </>
          )}

          {!!ax.armare.etrieri.zone?.length && (
            <>
              <h4>{t("struct.arf.etrierii_zone")}</h4>
              <table className="sg-table">
                <thead>
                  <tr>
                    <th>{t("struct.arl.th_zona")}</th>
                    <th>{t("struct.arf.pana")}</th>
                    <th>{t("struct.arf.v_maxim_zona")}</th>
                    <th>{t("struct.rzv.th_etrieri")}</th>
                    <th>{t("struct.arf.th_asw_efectiv")}</th>
                    <th>{t("struct.arf.th_asw_necesar")}</th>
                  </tr>
                </thead>
                <tbody>
                  {ax.armare.etrieri.zone.map((z) => (
                    <tr key={z.nr}>
                      <td>{z.eticheta}</td>
                      <td className="sg-mono">
                        {mFmt(z.s1_m)}…{mFmt(z.s2_m)} m ({mFmt(z.L_m)} m)
                      </td>
                      <td className="sg-mono">{nr(z.V_kn)} kN</td>
                      <td className="sg-mono">
                        {t("struct.arf.etrieri_ramuri", { phi: z.phi_mm, pas: z.pas_mm, ramuri: z.ramuri })}
                      </td>
                      <td className="sg-mono">{z.Asw_s_cm2_m} cm²/m</td>
                      <td className="sg-mono">
                        {z.Asw_s_necesar_cm2_m} cm²/m
                        {z.din_calcul === false && (
                          <span className="sg-muted"> {t("struct.arf.constructivi")}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <h4>{t("struct.arf.eforturile_au_cerut")}</h4>
          <table className="sg-table">
            <tbody>
              <tr>
                <td>{t("struct.arf.m_fibra_jos")}</td>
                <td className="sg-mono">{nr(ax.eforturi.M_poz_knm)} kN·m</td>
                <td className="sg-muted">{ax.eforturi.M_poz_combinatie}</td>
              </tr>
              <tr>
                <td>{t("struct.arf.m_fibra_sus")}</td>
                <td className="sg-mono">{nr(ax.eforturi.M_neg_knm)} kN·m</td>
                <td className="sg-muted">{ax.eforturi.M_neg_combinatie}</td>
              </tr>
              <tr>
                <td>V</td>
                <td className="sg-mono">{nr(ax.eforturi.V_kn)} kN</td>
                <td className="sg-muted">{ax.eforturi.V_combinatie}</td>
              </tr>
              <tr>
                <td>
                  {t("struct.arf.m_cvasipermanenta")}{" "}
                  <span className="sg-muted">{t("struct.arf.starea_verifica_fisura")}</span>
                </td>
                <td className="sg-mono">
                  {ax.eforturi.M_qp_knm == null
                    ? "—"
                    : `${nr(ax.eforturi.M_qp_knm)} kN·m`}
                </td>
                <td className="sg-muted">
                  {ax.fisura?.w_k_mm != null ? (
                    <>
                      {t("struct.arf.fisura_wk", {
                        wk: ax.fisura.w_k_mm,
                        sigma: ax.fisura.sigma_s_mpa,
                        fata: fataText(ax.fisura.fata_intinsa ?? ""),
                      })}
                    </>
                  ) : (
                    ax.fisura?.nota ?? ""
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          <h4>{t("struct.arf.armatura_aleasa")}</h4>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arf.pozitia")}</th>
                <th>{t("struct.arf.aleasa")}</th>
                <th>{t("struct.arf.th_as_efectiv")}</th>
                <th>{t("struct.arf.th_as_necesar")}</th>
                <th>{t("struct.arf.observatii")}</th>
              </tr>
            </thead>
            <tbody>
              {(
                [
                  ["struct.arf.longitudinale_jos", ax.armare.jos],
                  ["struct.arf.longitudinale_sus", ax.armare.sus],
                ] as [Cheie, Bare][]
              ).map(([et, b]) => (
                <tr key={et}>
                  <td>{t(et)}</td>
                  <td className="sg-mono">
                    {b.n}Ø{b.phi_mm}
                    {(b.randuri ?? 1) > 1 && (
                      <span className="sg-muted"> {t("struct.arf.randuri", { n: b.randuri })}</span>
                    )}
                  </td>
                  <td className="sg-mono">{b.As_cm2} cm²</td>
                  <td className="sg-mono">{b.As_necesar_cm2} cm²</td>
                  <td className="sg-muted">
                    {t("struct.arf.ancorare_innadire", {
                      a: mFmt(b.ancorare_m ?? 0), i: mFmt(b.innadire_m ?? 0),
                    })}
                    {!!b.note?.length && <> · {b.note.join("; ")}</>}
                  </td>
                </tr>
              ))}
              <tr>
                <td>{t("struct.arf.etrieri_inima")}</td>
                <td className="sg-mono">
                  {t("struct.arf.etrieri_ramuri", {
                    phi: ax.armare.etrieri.phi_mm,
                    pas: ax.armare.etrieri.pas_mm,
                    ramuri: ax.armare.etrieri.ramuri,
                  })}
                </td>
                <td className="sg-mono">{ax.armare.etrieri.Asw_s_cm2_m} cm²/m</td>
                <td className="sg-mono">
                  {ax.armare.etrieri.Asw_s_necesar_cm2_m} cm²/m
                </td>
                <td className="sg-muted">
                  {t("struct.arf.rho_pas_maxim", {
                    rho: ax.armare.etrieri.rho_w_pct,
                    pas: ax.armare.etrieri.pas_max_mm,
                  })}
                  {ax.armare.etrieri.din_calcul === false &&
                    " " + t("struct.arf.constructivi_vrdc")}
                </td>
              </tr>
              {(["jos", "sus"] as const).map((fata) => {
                const em = ax.armare.etrieri_montaj?.[fata];
                if (!em) return null;
                return (
                  <tr key={`montaj-${fata}`}>
                    <td>{t("struct.arf.etrier_montaj", { fata: fataText(fata) })}</td>
                    <td className="sg-mono">
                      Ø{em.phi_mm}/{ax.armare.etrieri.pas_mm} mm
                    </td>
                    <td className="sg-mono">—</td>
                    <td className="sg-mono">—</td>
                    <td className="sg-muted">
                      {t("struct.arf.montaj_leaga", {
                        bare: em.n_bare, pe_rand: em.n_pe_rand,
                        straturi: em.straturi, interax: em.interax_straturi_cm,
                        desf: mFmt(em.perimetru_m),
                      })}
                    </td>
                  </tr>
                );
              })}
              {ax.armare.plasa_talpa && (
                <tr>
                  <td>{t("struct.arf.transversale_talpa")}</td>
                  <td className="sg-mono">
                    Ø{ax.armare.plasa_talpa.phi_mm}/
                    {ax.armare.plasa_talpa.pas_mm} mm
                  </td>
                  <td className="sg-mono">
                    {ax.armare.plasa_talpa.As_cm2_m} cm²/m
                  </td>
                  <td className="sg-mono">
                    {ax.armare.plasa_talpa.As_necesar_cm2_m} cm²/m
                  </td>
                  <td className="sg-muted">
                    {t("struct.arf.consola_talpii_np")}
                  </td>
                </tr>
              )}
              {ax.armare.repartitie_talpa && (
                <tr>
                  <td>{t("struct.arf.repartitie_talpa")}</td>
                  <td className="sg-mono">
                    {ax.armare.repartitie_talpa.n}Ø
                    {ax.armare.repartitie_talpa.phi_mm}
                  </td>
                  <td colSpan={3} className="sg-muted">
                    {t("struct.arf.tin_transversalele")}
                  </td>
                </tr>
              )}
              {ax.armare.laterale && (
                <tr>
                  <td>{t("struct.arf.armaturi_laterale")}</td>
                  <td className="sg-mono">
                    {t("struct.arf.laterale_la", {
                      n: ax.armare.laterale.n,
                      phi: ax.armare.laterale.phi_mm,
                      pas: ax.armare.laterale.pas_mm,
                    })}
                  </td>
                  <td colSpan={3} className="sg-muted">
                    {t("struct.arf.fetele_inimii")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {!faraVederi && (<>
          <h4>{t("struct.arf.verificarile_axului")}</h4>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arg.th_verificarea")}</th>
                <th>{t("struct.arf.unde_ax")}</th>
                <th>{t("struct.arf.th_e_cerut")}</th>
                <th>{t("struct.arf.th_r_capabil")}</th>
                <th>R/E</th>
                <th>{t("struct.arg.th_verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {ax.verificari.map((ver, i) => (
                <tr key={i}>
                  <td>
                    <TitluVerificare
                      catalog={catalog}
                      pas="armare_fundatie"
                      nume={ver.nume}
                      grupa={ver.grupa}
                      aratPasul={false}
                      text={ver.nume}
                    />
                    {ver.nota && <div className="sg-muted">{ver.nota}</div>}
                    {ver.combinatie && (
                      <div className="sg-muted">
                        {t("struct.arp.combinatia")} {ver.combinatie}
                      </div>
                    )}
                  </td>
                  <td className="sg-mono" style={{ fontSize: 11 }}>
                    {ver.punct ?? "—"}
                  </td>
                  <td className="sg-mono">
                    {ver.E} {ver.unitate}
                  </td>
                  <td className="sg-mono">
                    {ver.R} {ver.unitate}
                  </td>
                  <td className="sg-mono">{ver.RE}</td>
                  <td>
                    <Verdict v={ver.verdict} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>)}

          {!!ax.pozitii?.length && (
            <>
              <h4>{t("struct.arf.otelul_axului_pozitii")}</h4>
              <table className="sg-table">
                <thead>
                  <tr>
                    <th>{t("struct.arf.pozitia")}</th>
                    <th>{t("struct.arf.bucati")}</th>
                    <th>Ø</th>
                    <th>{t("struct.arf.th_lungimea_barei")}</th>
                    <th>kg</th>
                  </tr>
                </thead>
                <tbody>
                  {ax.pozitii.map((p, i) => (
                    <tr key={i}>
                      <td>{p.pozitie}</td>
                      <td className="sg-mono">{p.n}</td>
                      <td className="sg-mono">Ø{p.phi_mm}</td>
                      <td className="sg-mono">{mFmt(p.l_bara_m)} m</td>
                      <td className="sg-mono">{p.kg}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={4}>
                      <b>{t("struct.ret.total")}</b>
                    </td>
                    <td className="sg-mono">
                      <b>{nr(ax.otel_kg)} kg</b>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </div>
  );
}

export default function StepArmareFundatie({
  state,
  running,
  focus,
  projectId,
  solutie,
}: {
  state: StepState | null;
  running: boolean;
  focus?: Focus;
  projectId?: string;
  solutie?: string | null;
}) {
  useT();
  const r = (state?.result ?? null) as Rezultat | null;
  const axe = useMemo(() => r?.axe ?? [], [r]);
  const [sel, setSel] = useState<string | null>(null);

  // linkul dintr-o verificare picată aduce chiar id-ul elementului („F-Ax 4")
  useEffect(() => {
    if (!focus?.ref) return;
    const gasit = axe.find(
      (a) => a.element === focus.ref || a.ax === focus.ref
    );
    if (gasit) setSel(gasit.element);
  }, [focus?.ref, focus?.n, axe]);

  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.arf.pasul_ruleaza")}</strong>
          <div className="sg-muted">
            {t("struct.arf.armeaza_grinzile_fundare")}
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.arf.nerulat")}
        <DescriereaPasului cheie="armare_fundatie" />
      </div>
    );
  }

  if (!r || !r.activ) {
    return (
      <div className="sg-muted st-empty">
        {r?.motiv ?? t("struct.arf.fara_rezultate")}
        {projectId && (
          <p>
            {t("struct.arf.cauza_incastrata")}{" "}
            <a href={hashSectiune(projectId, "model_fem", solutie)}>{t("struct.pas.scurt", { nr: 7 })}</a>
            {t("struct.arf.iar_cauza_lui_la")}{" "}
            <a href={hashSectiune(projectId, "rezistenta_fundatie", solutie)}>
              {t("struct.pas.scurt", { nr: 6 })}
            </a>
            .
          </p>
        )}
        <DescriereaPasului cheie="armare_fundatie" />
      </div>
    );
  }

  const ax = axe.find((a) => a.element === sel) ?? axe[0] ?? null;
  const v = r.verdicte || {};

  return (
    <div className="st-wrap">
      <div className="st-meta">
        <span>
          {tx("struct.arf.axe_armate", { n: r.nr_axe })}{" "}
          {Object.entries(v)
            .filter(([, n]) => n)
            .map(([k, n]) => `${n} × ${k}`)
            .join(" · ")}{" "}
          {t("struct.arf.otel_pe_beton", {
            otel: nr(r.otel_total_kg, 0),
            beton: nr(r.beton_total_m3, 1),
            kgm3: nr(r.otel_kg_m3, 1),
          })}
        </span>
        <span className="sg-muted">{t("struct.arf.rulat", { data: fmtDate(state.updated_at) })}</span>
      </div>

      {r.expunere && (
        <Info
          titlu={t("struct.arf.clasa_expunere", { clasa: r.expunere.clasa })}
          ce={r.expunere.eticheta ?? ""}
          deCe={t("struct.arf.expunere_de_ce", { motiv: traduceRezultat(r.expunere.motiv ?? "") })}
        />
      )}

      {!!r.avertismente?.length && (
        <div className="st-findings">
          <h3>{t("struct.ars.avertismente")}</h3>
          {r.avertismente.map((a, i) => (
            <div key={i} className="st-warn">
              • {a}
            </div>
          ))}
        </div>
      )}

      {/* --- tabelul axelor ----------------------------------------------- */}
      <div className="st-findings">
        <h3>{t("struct.arf.axele_de_fundare")}</h3>
        <table className="sg-table">
          <thead>
            <tr>
              <th>{t("struct.rzv.th_ax")}</th>
              <th>L</th>
              <th>{t("struct.arf.b_h_inima")}</th>
              <th>{t("struct.arf.th_cota")}</th>
              <th>M⁺ / M⁻ / V</th>
              <th>{t("insp.rand.jos")}</th>
              <th>{t("insp.rand.sus")}</th>
              <th>{t("struct.rzv.th_etrieri")}</th>
              <th>{t("struct.arf.otel")}</th>
              <th>{t("struct.arg.th_verdict")}</th>
            </tr>
          </thead>
          <tbody>
            {axe.map((a) => (
              <tr
                key={a.element}
                onClick={() => setSel(a.element)}
                style={{
                  cursor: "pointer",
                  background:
                    a.element === (ax?.element ?? "")
                      ? "var(--sel, rgba(0,0,0,.04))"
                      : undefined,
                }}
              >
                <td>
                  <b>{a.ax}</b>
                </td>
                <td>{mFmt(a.L_m)} m</td>
                <td className="sg-mono">
                  {mFmt(a.B_m)} × {mFmt(a.H_c_m)} m · {cmFmt(a.b_inima_m)}
                </td>
                <td className="sg-mono">−{mFmt(a.Df_m)} m</td>
                <td className="sg-mono">
                  {nr(a.eforturi.M_poz_knm)} / {nr(a.eforturi.M_neg_knm)} kN·m ·{" "}
                  {nr(a.eforturi.V_kn)} kN
                </td>
                {(["jos", "sus"] as const).map((fata) => (
                  <td className="sg-mono" key={fata}>
                    {a.armare[fata].n}Ø{a.armare[fata].phi_mm}
                    {!!a.armare.suplimentare?.[fata]?.length && (
                      <span className="sg-muted">
                        {" "}
                        {t("struct.arf.supl", { n: a.armare.suplimentare[fata].length })}
                      </span>
                    )}
                  </td>
                ))}
                <td className="sg-mono">
                  Ø{a.armare.etrieri.phi_mm}/{a.armare.etrieri.pas_mm}
                  {(a.armare.etrieri.zone?.length ?? 0) > 1 && (
                    <span className="sg-muted">
                      {" "}
                      {t("struct.arf.zone", { n: a.armare.etrieri.zone!.length })}
                    </span>
                  )}
                </td>
                <td className="sg-mono">{nr(a.otel_kg)} kg</td>
                <td>
                  <Verdict v={a.verdict} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* --- axul selectat ------------------------------------------------ */}
      {ax && <CardAx ax={ax} r={r} projectId={projectId} solutie={solutie} />}

      {!!r.nu_se_aplica?.length && (
        <div className="st-findings">
          <h3>{t("struct.arf.aplica_grinzii_fundare")}</h3>
          <p className="sg-muted">
            {t("struct.arf.cerinte_le_are")}
          </p>
          <table className="sg-table">
            <tbody>
              {r.nu_se_aplica.map((x, i) => (
                <tr key={i}>
                  <td>{x.cerinta}</td>
                  <td className="sg-muted">{x.de_ce}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DescriereaPasului cheie="armare_fundatie" />
    </div>
  );
}
